import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type Pack } from '../api'

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function typeLabel(type: string) {
  if (type === 'resources') return 'Resource'
  if (type === 'data' || type === 'javascript') return 'Behavior'
  return type
}

function typeBadgeClass(type: string) {
  return type === 'resources' ? 'badge-green' : 'badge-gray'
}

function UploadModal({ onClose, onUploaded }: { onClose: () => void; onUploaded: () => void }) {
  const [file,        setFile]        = useState<File | null>(null)
  const [name,        setName]        = useState('')
  const [description, setDescription] = useState('')
  const [step,        setStep]        = useState<'pick' | 'uploading' | 'registering'>('pick')
  const [progress,    setProgress]    = useState('')
  const [error,       setError]       = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => { fileRef.current?.focus() }, [])

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null
    setFile(f)
    if (f && !name) {
      setName(f.name.replace(/\.(mcpack|mcaddon)$/i, ''))
    }
    setTimeout(() => nameRef.current?.focus(), 50)
  }

  async function upload(e: React.FormEvent) {
    e.preventDefault()
    if (!file) return
    setError(null)

    try {
      setStep('uploading')
      setProgress('Getting upload URL…')
      const { packId, uploadUrl } = await api.getPackUploadUrl(file.name)

      setProgress('Uploading file to S3…')
      const s3Resp = await fetch(uploadUrl, {
        method:  'PUT',
        body:    file,
        headers: { 'Content-Type': 'application/octet-stream' },
      })
      if (!s3Resp.ok) throw new Error(`S3 upload failed: HTTP ${s3Resp.status}`)

      setStep('registering')
      setProgress('Registering pack…')
      await api.registerPack({
        packId,
        fileName:    file.name,
        name:        name.trim(),
        description: description.trim() || undefined,
      })

      onUploaded()
      onClose()
    } catch (e) {
      setError((e as Error).message)
      setStep('pick')
      setProgress('')
    }
  }

  const busy = step !== 'pick'

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget && !busy) onClose() }}>
      <div className="modal">
        <div className="modal-header">
          <h2>Upload pack</h2>
          <button className="modal-close" onClick={onClose} disabled={busy}>×</button>
        </div>
        <form onSubmit={upload}>
          <div className="form-stack">
            <div className="form-group">
              <label>Pack file</label>
              <input
                ref={fileRef}
                type="file"
                accept=".mcpack,.mcaddon"
                onChange={onFileChange}
                disabled={busy}
                required
              />
              <p className="text-xs text-muted mt-1">Accepts .mcpack and .mcaddon files</p>
            </div>
            <div className="form-group">
              <label>Display name</label>
              <input
                ref={nameRef}
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Better Textures"
                disabled={busy}
                required
              />
            </div>
            <div className="form-group">
              <label>Description <span className="text-muted">(optional)</span></label>
              <input
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Short description"
                disabled={busy}
              />
            </div>
          </div>
          {busy && <p className="text-sm text-muted mt-3"><span className="spinning">◌</span> {progress}</p>}
          {error && <p className="error-msg mt-3">⚠ {error}</p>}
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-green" disabled={busy || !file || !name.trim()}>
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function DeleteConfirmModal({ pack, onConfirm, onClose }: {
  pack:      Pack
  onConfirm: () => Promise<void>
  onClose:   () => void
}) {
  const [deleting, setDeleting] = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  async function confirm() {
    setDeleting(true)
    setError(null)
    try {
      await onConfirm()
    } catch (e) {
      setError((e as Error).message)
      setDeleting(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget && !deleting) onClose() }}>
      <div className="modal">
        <div className="modal-header">
          <h2>Delete "{pack.name}"?</h2>
          <button className="modal-close" onClick={onClose} disabled={deleting}>×</button>
        </div>
        <p className="text-sm text-muted">
          This removes the pack from the library and deletes the file from S3.
          You'll need to re-upload it to use it again.
        </p>
        {error && <p className="error-msg mt-3">⚠ {error}</p>}
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={confirm} disabled={deleting}>
            {deleting ? <><span className="spinning">◌</span> Deleting…</> : 'Delete pack'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Packs() {
  const [packs,       setPacks]       = useState<Pack[]>([])
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState<string | null>(null)
  const [showUpload,  setShowUpload]  = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Pack | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setPacks(await api.getPacks())
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function deletePack(pack: Pack) {
    await api.deletePack(pack.packId)
    setDeleteTarget(null)
    load()
  }

  return (
    <>
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Packs</h1>
          <p>Upload resource and behavior packs. Apply them to worlds from the Worlds page.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
            {loading ? <span className="spinning">◌</span> : '↻'} Refresh
          </button>
          <button className="btn btn-green btn-sm" onClick={() => setShowUpload(true)}>
            + Upload pack
          </button>
        </div>
      </div>

      {error && <p className="error-msg mb-4">⚠ {error}</p>}

      {loading && packs.length === 0 ? (
        <p className="text-muted text-sm">Loading…</p>
      ) : packs.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <p className="text-muted">No packs uploaded yet.</p>
          <button className="btn btn-green btn-sm mt-3" onClick={() => setShowUpload(true)}>
            Upload your first pack
          </button>
        </div>
      ) : (
        <div className="world-list">
          {packs.map(p => (
            <div key={p.packId} className="world-card">
              <div style={{ fontSize: '1.4rem' }}>📦</div>

              <div className="world-info">
                <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  <span className="world-name">{p.name}</span>
                  {p.components.map((c, i) => (
                    <span key={i} className={`badge ${typeBadgeClass(c.type)}`}>
                      {typeLabel(c.type)}
                    </span>
                  ))}
                </div>
                <div className="world-meta">
                  {p.fileName} · Uploaded {fmtDate(p.uploadedAt)}
                  {p.description && <> · {p.description}</>}
                </div>
                {p.components.length > 0 && (
                  <div className="text-xs text-muted mt-1">
                    {p.components.map(c => (
                      <span key={c.uuid} className="font-mono" style={{ marginRight: 8 }}>
                        {c.uuid.substring(0, 8)}… v{c.version.join('.')}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ flexShrink: 0 }}>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => setDeleteTarget(p)}
                  title="Delete pack"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={load}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          pack={deleteTarget}
          onConfirm={() => deletePack(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </>
  )
}
